import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { identity, pickBy } from 'lodash';
import {
  withAccountHeader,
  withAuthHeader,
  withRetries,
  withConfiguredBaseUrl,
  compose,
  withE2ERequestStubbing,
} from '../hooks/useService';
import { ServiceAuth } from '../hooks/useService/useService';

import urls from '../util/urls';

export type CodingSearchParams = {
  projectId: string;
  limit?: number;
  code?: string;
  system?: string;
};

export type GlobalCodingSearchParams = {
  limit?: number;
  code?: string;
  system?: string;
};

export type ParentTraversalRelationship = {
  parentId: string;
  childId: string;
};

export type ParentTraversalResult = {
  relationships: ParentTraversalRelationship[];
  codings: TerminologyServiceCoding[];
};

export type RelationshipCount = {
  count: number;
};

export type RelationshipCounts = {
  subsumes?: RelationshipCount;
  specializes?: RelationshipCount;
};

export type TerminologyServiceCoding = {
  relationships?: RelationshipCounts;
  extension?: string | null;
  system: string;
  code: string;
  display?: string;
  id: string;
  version?: string | null;
};

export type TraverseResult = {
  items: TerminologyServiceCoding[];
};

export interface ContactDetail extends fhir.ContactDetail {
  telecom?: [fhir.ContactPoint];
}

export type BulkParentTraversalIdEntry = {
  id: string;
};

export type BulkParentTraversalCodeSystemEntry = {
  system: string;
  code: string;
};

export type BulkParentTraversalEntry =
  | BulkParentTraversalIdEntry
  | BulkParentTraversalCodeSystemEntry;

export type BulkParentTraversalRequest = BulkParentTraversalEntry[];

export type Availability = 'project' | 'account' | 'public';

export type ProjectCodingSearchSource = {
  resourceType: string;
  propertyPath: string;
};

export type ProjectCodingSearchRequest = {
  searchSources: ProjectCodingSearchSource[];
  searchText?: string;
  fuzzyEditDistance?: number;
};

const emptyStringToNull = (s: string): string | null => (s.length ? s : null);

const codingSearchCache: Record<
  string,
  AxiosResponse<TerminologyServiceCoding[]>
> = {};

export default class TerminologySearchService {
  accessor: (auth: ServiceAuth) => AxiosInstance;

  constructor(_options?: Partial<AxiosRequestConfig>) {
    this.accessor = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create());
  }

  getCodeSystem =
    (auth: ServiceAuth) =>
    async (params: { project: string } & { id: string }) => {
      const { project, id } = params;
      const endpoint = urls.api.ontology.system(project, id);
      return this.accessor(auth).get(endpoint);
    };

  createCodeSystem =
    (auth: ServiceAuth) =>
    async (
      params: { project: string } & {
        title: string;
        description: string;
        url: string;
        version?: string;
        contact?: [ContactDetail];
        publisher?: string;
        copyright?: string;
      },
    ) => {
      const {
        project,
        title,
        description,
        url,
        version,
        contact,
        publisher,
        copyright,
      } = params;

      const endpoint = urls.api.ontology.systems(project);
      return this.accessor(auth).post(endpoint, {
        title,
        description,
        url,
        version: version ? version : undefined,
        contact,
        publisher: publisher || undefined,
        copyright: copyright || undefined,
      });
    };

  deleteCodeSystem =
    (auth: ServiceAuth) => async (params: { project: string; id: string }) => {
      const { project, id } = params;
      const endpoint = urls.api.ontology.system(project, id);
      return this.accessor(auth).delete(endpoint);
    };

  updateCodeSystem =
    (auth: ServiceAuth) =>
    async (params: {
      project: string;
      id: string;
      title?: string | null;
      description?: string | null;
      contact?: [ContactDetail] | null;
      publisher?: string | null;
      copyright?: string | null;
    }) => {
      const { project, id, contact, title, description, publisher, copyright } =
        params;
      const endpoint = urls.api.ontology.system(project, id);
      return this.accessor(auth).patch(endpoint, {
        // assume that an empty string means that the field is being removed
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        title: emptyStringToNull(title),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        description: emptyStringToNull(description),
        contact,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        publisher: emptyStringToNull(publisher),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        copyright: emptyStringToNull(copyright),
      });
    };

  listLocalCodeSystems =
    (auth: ServiceAuth) =>
    async (params: { projectId: string; limit: number }) => {
      const { limit, projectId } = params;
      const endpoint = urls.api.ontology.localSystems(projectId, {
        limit,
      });
      return this.accessor(auth).get(endpoint);
    };

  listCodeSystems =
    (auth: ServiceAuth) =>
    async (params: {
      projectId: string;
      availability: Array<string>;
      limit: number;
      includeHasAssociation?: boolean;
    }) => {
      const { projectId, availability, limit, includeHasAssociation } = params;
      const endpoint = urls.api.ontology.systems(projectId, {
        availability,
        limit,
        includeHasAssociation,
      });
      return this.accessor(auth).get(endpoint);
    };

  listAssociatedCodeSystems =
    (auth: ServiceAuth) =>
    async (params: { pageSize?: string; projectId: string }) => {
      const { projectId, ...rest } = params;
      return this.accessor(auth).get(
        urls.api.ontology.associatedSystems(projectId, rest),
      );
    };

  getValueSet =
    (auth: ServiceAuth) =>
    async (params: { project: string } & { id: string }) => {
      const { project, id } = params;
      const endpoint = urls.api.ontology.valueSet(project, id);
      return this.accessor(auth).get(endpoint);
    };

  createValueSet =
    (auth: ServiceAuth) =>
    async (
      params: { project: string } & {
        title: string;
        description: string;
        url: string;
        version?: string;
        contact?: [ContactDetail];
        publisher?: string;
        copyright?: string;
      },
    ) => {
      const {
        project,
        title,
        description,
        url,
        version,
        contact,
        publisher,
        copyright,
      } = params;
      const endpoint = urls.api.ontology.valueSets(project);
      return this.accessor(auth).post(endpoint, {
        title,
        description,
        url,
        version: version ? version : undefined,
        contact,
        publisher: publisher || undefined,
        copyright: copyright || undefined,
      });
    };

  deleteValueSet =
    (auth: ServiceAuth) => async (params: { project: string; id: string }) => {
      const { project, id } = params;
      const endpoint = urls.api.ontology.valueSet(project, id);
      return this.accessor(auth).delete(endpoint);
    };

  updateValueSet =
    (auth: ServiceAuth) =>
    async (params: {
      project: string;
      id: string;
      title?: string | null;
      description?: string | null;
      contact?: [ContactDetail] | null;
      publisher?: string | null;
      copyright?: string | null;
    }) => {
      const { project, id, title, description, contact, publisher, copyright } =
        params;
      const endpoint = urls.api.ontology.valueSet(project, id);
      return this.accessor(auth).patch(endpoint, {
        // assume that an empty string means that the field is being removed
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        title: emptyStringToNull(title),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        description: emptyStringToNull(description),
        contact,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        publisher: emptyStringToNull(publisher),
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        copyright: emptyStringToNull(copyright),
      });
    };

  listLocalValueSets =
    (auth: ServiceAuth) =>
    async (params: { projectId: string; limit: number }) => {
      const { limit, projectId } = params;
      const endpoint = urls.api.ontology.localValueSets(projectId, {
        limit,
      });
      return this.accessor(auth).get(endpoint);
    };

  listValueSets =
    (auth: ServiceAuth) =>
    async (params: {
      projectId: string;
      availability: Array<string>;
      limit: number;
      includeHasAssociation?: boolean;
    }) => {
      const { projectId, availability, limit, includeHasAssociation } = params;
      const endpoint = urls.api.ontology.valueSets(projectId, {
        availability,
        limit,
        includeHasAssociation,
      });
      return this.accessor(auth).get(endpoint);
    };

  listAssociatedValueSets =
    (auth: ServiceAuth) =>
    async (params: { projectId: string; pageSize?: number }) => {
      const { projectId, ...rest } = params;
      return this.accessor(auth).get(
        urls.api.ontology.associatedValueSets(projectId, rest),
      );
    };

  createCoding =
    (auth: ServiceAuth) =>
    async ({
      project,
      system,
      code,
      display,
      version,
    }: { project: string } & fhir.Coding) => {
      // no query parameters for POST create-codings
      const url = urls.api.ontology.codings(project, {});
      return this.accessor(auth).post<fhir.Coding[]>(url, {
        system,
        code,
        display,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        version: emptyStringToNull(version),
      });
    };

  searchCodings =
    (auth: ServiceAuth) =>
    async ({ projectId, ...search }: CodingSearchParams) => {
      const url = urls.api.ontology.codings(projectId, search);
      if (!codingSearchCache[url]) {
        codingSearchCache[url] = await this.accessor(auth).get<
          TerminologyServiceCoding[]
        >(url);
      }
      return codingSearchCache[url];
    };

  searchGlobalCodings =
    (auth: ServiceAuth) => async (search: GlobalCodingSearchParams) => {
      const url = urls.api.ontology.globalCodings(search);
      if (!codingSearchCache[url]) {
        codingSearchCache[url] = await this.accessor(auth).get<
          TerminologyServiceCoding[]
        >(url);
      }
      return codingSearchCache[url];
    };

  getCodeSystemCodings =
    (auth: ServiceAuth) =>
    async ({
      ontologyId,
      project,
      system,
      version,
      limit = 100,
      display,
      nextPageToken,
    }: {
      ontologyId: string;
      project: string;
      limit: number;
      nextPageToken: number;
    } & Pick<fhir.Coding, 'system' | 'version' | 'display'>) => {
      const coding = pickBy({ system, version }, identity);
      const url = urls.api.ontology.codeSystemCodings(project, ontologyId, {
        ...coding,
        pageSize: limit,
        display,
        nextPageToken,
      });
      return this.accessor(auth).get(url);
    };

  getCodeSystemCodingsRelatedCodings =
    (auth: ServiceAuth) =>
    async (
      projectId: string,
      codeSystemId: string,
      codingId: string,
      equivalence: string,
    ) => {
      return this.accessor(auth).get(
        urls.api.ontology.codingsRelatedCodings(
          projectId,
          codeSystemId,
          codingId,
          {
            equivalence,
          },
        ),
      );
    };

  traverseCodings =
    (auth: ServiceAuth) =>
    async (
      projectId: string,
      codeId: string,
      equivalence: 'subsumes' | 'specializes',
      depth: number,
    ) => {
      return this.accessor(auth).get<TraverseResult>(
        urls.api.ontology.traverseCodings(projectId, codeId, {
          equivalence,
          depth,
        }),
      );
    };

  createCodeSystemAssociation =
    (auth: ServiceAuth) =>
    async (params: { project: string; codeSystemId: string }) => {
      const { project, codeSystemId } = params;
      const url = urls.api.ontology.modifyCodeSystemAssociation(
        project,
        codeSystemId,
      );

      return this.accessor(auth).post(url);
    };

  createValueSetAssociation =
    (auth: ServiceAuth) =>
    async (params: { project: string; valueSetId: string }) => {
      const { project, valueSetId } = params;
      const url = urls.api.ontology.modifyValueSetAssociation(
        project,
        valueSetId,
      );

      return this.accessor(auth).post(url);
    };

  deleteCodeSystemAssociation =
    (auth: ServiceAuth) =>
    async (params: { project: string; codeSystemId: string }) => {
      const { project, codeSystemId } = params;
      const url = urls.api.ontology.modifyCodeSystemAssociation(
        project,
        codeSystemId,
      );

      return this.accessor(auth).delete(url);
    };

  deleteValueSetAssociation =
    (auth: ServiceAuth) =>
    async (params: { project: string; valueSetId: string }) => {
      const { project, valueSetId } = params;
      const url = urls.api.ontology.modifyValueSetAssociation(
        project,
        valueSetId,
      );

      return this.accessor(auth).delete(url);
    };

  getValueSetCodings =
    (auth: ServiceAuth) =>
    async ({
      ontologyId,
      project,
      system,
      version,
      limit = 100,
      display,
    }: {
      ontologyId: string;
      project: string;
      limit: number;
    } & Pick<fhir.Coding, 'system' | 'version' | 'display'>) => {
      if (display && !display.endsWith('*')) {
        display = display.concat('*');
      }
      const coding = pickBy({ system, version }, identity);
      const url = urls.api.ontology.valueSetCodings(project, ontologyId, {
        ...coding,
        pageSize: limit,
        display,
      });
      return this.accessor(auth).get(url);
    };

  addValueSetCodings =
    (auth: ServiceAuth) =>
    async (params: {
      codingIds: Array<string>;
      project: string;
      destinationValueSetId: string;
      sourceCodeSystemId: string;
    }) => {
      const { codingIds, destinationValueSetId, project, sourceCodeSystemId } =
        params;
      const endpoint = urls.api.ontology.valueSetCodings(
        project,
        destinationValueSetId,
      );
      return this.accessor(auth).patch(endpoint, {
        codeSystemId: sourceCodeSystemId,
        codingIds,
      });
    };

  deleteValueSetCodings =
    (auth: ServiceAuth) =>
    async (params: {
      codingIds: Array<string>;
      project: string;
      destinationValueSetId: string;
    }) => {
      const { codingIds, destinationValueSetId, project } = params;
      const endpoint = urls.api.ontology.valueSetCodings(
        project,
        destinationValueSetId,
        { id: codingIds },
      );
      return this.accessor(auth).delete(endpoint);
    };

  bulkTraverseParents =
    (auth: ServiceAuth) =>
    async (projectId: string, request: BulkParentTraversalRequest) => {
      return this.accessor(auth).post<ParentTraversalResult>(
        urls.api.ontology.bulkTraverseParents(projectId),
        request,
      );
    };

  globalBulkTraverseParents =
    (auth: ServiceAuth) => async (request: BulkParentTraversalRequest) => {
      return this.accessor(auth).post<ParentTraversalResult>(
        urls.api.ontology.globalBulkTraverseParents(),
        request,
      );
    };

  projectCodings =
    (auth: ServiceAuth) =>
    async (projectId: string, request: ProjectCodingSearchRequest) => {
      return this.accessor(auth).post<{ items: fhir.Coding[] }>(
        urls.api.ontology.projectCodings(projectId),
        request,
      );
    };
}
